import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@/components/ui/Modal';
import { useRenameTakeawayMutation } from '@/features/waiter/waiterApi';
import { useModalBackClose } from './useModalBackClose';

interface Props {
  open: boolean;
  noteUuid: string;
  token: string;
  /** Pre-filled unless the note still carries its « Client N » placeholder. */
  currentLabel: string;
  provisional: boolean;
  /** True when opened by itself right after the order went out (« Plus
   *  tard ») — adds the one line saying why it opened. */
  afterOrder: boolean;
  onClose: () => void;
  onRenamed: (label: string) => void;
}

/**
 * SC-1466 — the client's name of an Emporter note, given after the order:
 * opens by itself once the first round of a « Plus tard » note is sent, and
 * from the label in the ticket header at any time. Same field rules as the
 * « + » creation (`NoteCreateModals`): 30 chars, not blank, unique among
 * open notes (409 LABEL_IN_USE from the server).
 */
export default function TakeawayNameModal({ open, ...rest }: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={rest.onClose}
      ariaLabel={t('waiter.ticket.name.title')}
      instant
      dismissOnBackdrop={false}
    >
      {open && <TakeawayNameBody {...rest} />}
    </Modal>
  );
}

function TakeawayNameBody({
  noteUuid,
  token,
  currentLabel,
  provisional,
  afterOrder,
  onClose,
  onRenamed,
}: Omit<Props, 'open'>) {
  const { t } = useTranslation();
  const [label, setLabel] = useState(provisional ? '' : currentLabel);
  const [error, setError] = useState<string | null>(null);
  const [rename, { isLoading }] = useRenameTakeawayMutation();
  // Android back closes the dialog, like every other sheet of the ticket.
  useModalBackClose('takeaway-name', onClose);
  // Closed while the PATCH was in flight: the name is saved server-side (and
  // the poll brings it back), but the ticket on screen may no longer be this
  // note — leave it alone.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleSubmit = async () => {
    const trimmed = label.trim();
    if (!trimmed) {
      setError(t('waiter.ticket.name.empty'));
      return;
    }
    if (!provisional && trimmed === currentLabel) {
      onClose();
      return;
    }
    setError(null);
    try {
      const result = await rename({ noteUuid, label: trimmed, token }).unwrap();
      if (mountedRef.current) onRenamed(result.label);
    } catch (err) {
      if (!mountedRef.current) return;
      const apiErr = err as { status?: number; data?: { code?: string } };
      if (apiErr.data?.code === 'LABEL_IN_USE') {
        setError(t('waiter.notes.errors.labelInUse'));
      } else if (apiErr.data?.code === 'ALREADY_HANDED_OFF') {
        // The caisse holds a snapshot under the current name.
        setError(t('waiter.ticket.name.handedOff'));
      } else {
        setError(t('waiter.notes.errors.generic'));
      }
    }
  };

  return (
    <div data-testid="takeaway-name-modal" className="p-5 flex flex-col gap-4 md:max-w-[400px] md:mx-auto md:w-full">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-sans text-[18px] font-bold">{t('waiter.ticket.name.title')}</h2>
        <button
          type="button"
          data-testid="takeaway-name-close"
          aria-label={t('common.close')}
          onClick={onClose}
          className="-me-2 w-11 h-11 shrink-0 flex items-center justify-center font-sans text-2xl leading-none text-ink"
        >
          ×
        </button>
      </div>
      {afterOrder && (
        <p data-testid="takeaway-name-hint" className="font-sans text-base text-ink-mute">
          {t('waiter.ticket.name.afterOrderHint', { label: currentLabel })}
        </p>
      )}
      <input
        data-testid="takeaway-name-input"
        type="text"
        inputMode="text"
        autoCapitalize="words"
        autoComplete="off"
        enterKeyHint="done"
        maxLength={30}
        autoFocus
        placeholder={t('waiter.notes.createTakeaway.placeholder')}
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        className="border-2 border-ink rounded-btn-sm px-3 py-2 text-lg bg-paper text-ink"
      />
      {error && (
        <p data-testid="takeaway-name-error" role="alert" className="font-mono text-sm text-red-600">
          {error}
        </p>
      )}
      <div className="flex gap-3">
        <button
          type="button"
          data-testid="takeaway-name-cancel"
          onClick={onClose}
          className="flex-1 h-12 bg-paper border-2 border-ink rounded-btn px-4 font-sans text-[18px] font-bold"
        >
          {afterOrder ? t('waiter.ticket.name.later') : t('common.cancel')}
        </button>
        <button
          type="button"
          data-testid="takeaway-name-submit"
          disabled={isLoading}
          aria-busy={isLoading}
          onClick={() => void handleSubmit()}
          className="flex-1 h-12 bg-ink text-paper rounded-btn px-4 font-sans text-[18px] font-bold disabled:opacity-50"
        >
          {t('waiter.notes.submit')}
        </button>
      </div>
    </div>
  );
}
